import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Printer, Copy, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

export function QrCodeModal({ open, onClose, cardId, isAutomotive = false }) {
    const { t } = useTranslation();
    const qrRef = useRef(null);

    // Automotive cards use the permanent /vehicle/{card_id} tag URL; all other
    // cards use the standard tap resolver. The backend QR image endpoint
    // mirrors this same logic, so downloads match the on-screen code.
    const tapUrl = cardId
        ? (isAutomotive ? `${API_BASE}/vehicle/${cardId}` : `${API_BASE}/api/tap?card=${cardId}`)
        : '';
    const qrApiBase = `${API_BASE}/api/cards/qr/${cardId}`;

    const handleDownloadPng = useCallback(() => {
        const link = document.createElement('a');
        link.href = `${qrApiBase}?format=png&size=1024`;
        link.download = `qr-${cardId}.png`;
        link.click();
    }, [cardId, qrApiBase]);

    const handleDownloadSvg = useCallback(() => {
        const link = document.createElement('a');
        link.href = `${qrApiBase}?format=svg`;
        link.download = `qr-${cardId}.svg`;
        link.click();
    }, [cardId, qrApiBase]);

    const handlePrint = useCallback(() => {
        const printWindow = window.open('', '_blank', 'width=600,height=700');
        if (!printWindow) return;
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head><title>QR Code - ${cardId}</title>
            <style>
                body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; }
                .qr-container { text-align: center; padding: 40px; }
                .qr-container img { width: 400px; height: 400px; }
                .card-label { font-size: 24px; font-weight: bold; margin-top: 20px; letter-spacing: 2px; }
                .url-label { font-size: 11px; color: #666; margin-top: 8px; word-break: break-all; max-width: 400px; }
                @media print { body { margin: 0; } }
            </style>
            </head>
            <body>
                <div class="qr-container">
                    <img src="${qrApiBase}?format=png&size=1024" alt="QR Code" />
                    <div class="card-label">${cardId}</div>
                    <div class="url-label">${tapUrl}</div>
                </div>
                <script>
                    document.querySelector('img').onload = function() { window.print(); window.close(); };
                    setTimeout(function() { window.print(); window.close(); }, 3000);
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    }, [cardId, tapUrl, qrApiBase]);

    const handleCopyUrl = useCallback(() => {
        navigator.clipboard.writeText(tapUrl).then(() => {
            toast.success(t('cards.urlCopied'));
        });
    }, [tapUrl, t]);

    if (!cardId) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-sm" data-testid="qr-code-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Link2 className="w-4 h-4" />
                        {t('cards.qrCodeFor', { cardId })}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4">
                    {/* QR Code */}
                    <div
                        ref={qrRef}
                        className="bg-white p-4 rounded-xl"
                        data-testid="qr-code-image"
                    >
                        <QRCodeSVG
                            value={tapUrl}
                            size={220}
                            level="H"
                            includeMargin={false}
                        />
                    </div>

                    {/* Card ID */}
                    <p className="font-mono text-lg font-bold tracking-wider" data-testid="qr-card-id">
                        {cardId}
                    </p>

                    {/* Tap URL */}
                    <div className="w-full">
                        <p className="text-xs text-muted-foreground mb-1">{t('cards.tapUrl')}</p>
                        <code className="block text-xs bg-muted px-3 py-2 rounded-lg border break-all select-all" data-testid="qr-tap-url">
                            {tapUrl}
                        </code>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 w-full">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadPng}
                            className="gap-1.5"
                            data-testid="qr-download-png"
                        >
                            <Download className="w-3.5 h-3.5" />
                            {t('cards.downloadPng')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadSvg}
                            className="gap-1.5"
                            data-testid="qr-download-svg"
                        >
                            <Download className="w-3.5 h-3.5" />
                            {t('cards.downloadSvg')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrint}
                            className="gap-1.5"
                            data-testid="qr-print"
                        >
                            <Printer className="w-3.5 h-3.5" />
                            {t('cards.printQr')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCopyUrl}
                            className="gap-1.5"
                            data-testid="qr-copy-url"
                        >
                            <Copy className="w-3.5 h-3.5" />
                            {t('cards.copyUrl')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
